import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { BRAND, SOCIALS } from "@/data/content";
import SocialIcon from "@/components/SocialIcon";
import { Mail, Building2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Kiitos viestistäsi! Palaamme asiaan mahdollisimman pian.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Viestin lähetys epäonnistui. Yritäthän uudelleen.");
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "w-full rounded-2xl border border-border bg-white px-5 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors focus:border-primary";

  return (
    <main data-testid="contact-page" className="pt-28 md:pt-36 pb-24">
      <Seo
        title="Yhteystiedot – JINSU"
        description="Ota yhteyttä JINSU-tiimiin – vastaamme kysymyksiin tuotteista, tilauksista ja toimituksista."
      />

      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <p className="eyebrow text-water-deep/80">Yhteystiedot</p>
        <h1 className="h-display font-heading mt-5 font-semibold text-foreground" lang="fi">
          Ota yhteyttä.
        </h1>
        <p className="mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-muted-foreground">
          Kysymyksiä tuotteista, tilauksestasi tai toimituksesta? Lähetä viesti – palaamme asiaan
          mahdollisimman pian.
        </p>

        <div className="mt-14 grid gap-10 lg:grid-cols-12">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 rounded-[2rem] bg-white p-8 md:p-10 shadow-[0_10px_40px_-18px_rgba(23,68,94,0.2)]"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Nimi
                </label>
                <input
                  id="contact-name"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  placeholder="Etunimi Sukunimi"
                  className={fieldClass}
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Sähköposti
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  placeholder="nimi@esimerkki.fi"
                  className={fieldClass}
                  data-testid="contact-email-input"
                />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="contact-subject" className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Aihe
              </label>
              <input
                id="contact-subject"
                name="subject"
                required
                value={form.subject}
                onChange={onChange}
                placeholder="Mistä haluat kysyä?"
                className={fieldClass}
                data-testid="contact-subject-input"
              />
            </div>
            <div className="mt-5">
              <label htmlFor="contact-message" className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">
                Viesti
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                rows={6}
                value={form.message}
                onChange={onChange}
                placeholder="Kirjoita viestisi tähän…"
                className={`${fieldClass} resize-y`}
                data-testid="contact-message-input"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-primary px-8 py-3.5 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition-transform duration-200 hover:scale-[1.03] disabled:opacity-60 disabled:hover:scale-100"
            >
              <Send size={16} /> {sending ? "Lähetetään…" : "Lähetä viesti"}
            </button>
          </form>

          <aside className="lg:col-span-4 lg:col-start-9">
            <div className="rounded-[2rem] bg-water-soft p-8">
              <h2 className="font-heading text-xl font-semibold text-foreground">Yhteystiedot</h2>
              <ul className="mt-6 space-y-5 text-sm leading-relaxed">
                <li className="flex items-start gap-3.5">
                  <Building2 size={18} className="mt-0.5 shrink-0 text-water-deep" />
                  <span>
                    <strong className="block text-foreground">{BRAND.company}</strong>
                    <span className="text-muted-foreground">Y-tunnus {BRAND.businessId}</span>
                  </span>
                </li>
                <li className="flex items-start gap-3.5">
                  <Mail size={18} className="mt-0.5 shrink-0 text-water-deep" />
                  <a
                    href={`mailto:${BRAND.email}`}
                    className="font-semibold text-primary transition-colors hover:text-water-deep"
                    data-testid="contact-email-link"
                  >
                    {BRAND.email}
                  </a>
                </li>
                {SOCIALS.map((s) => (
                  <li key={s.name} className="flex items-start gap-3.5">
                    <span className="mt-0.5 shrink-0 text-water-deep">
                      <SocialIcon name={s.name} size={18} strokeWidth={1.8} />
                    </span>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-primary transition-colors hover:text-water-deep"
                      data-testid={`contact-social-${s.name.toLowerCase()}`}
                    >
                      {s.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-6 rounded-[2rem] bg-white p-8 shadow-sm">
              <h2 className="font-heading text-xl font-semibold text-foreground">Asiakaspalvelu</h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Vastaamme viesteihin arkisin. Tilauksiin liittyvissä asioissa mainitsethan
                tilausnumerosi.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
